import React, { useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { HandHeart, Calendar, ClipboardList, Clock, LayoutDashboard, LogOut, Menu, X, User, Timer, AlertCircle, Mail } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import NotificationBell from './NotificationBell';
import { toast } from 'sonner';

const Navbar = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;
  const [resending, setResending] = useState(false);

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`);
      toast.success('Verification email sent! Check your inbox.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setResending(false);
    }
  };

  const NavLink = ({ to, icon: Icon, children, testId }) => (
    <Link
      to={to}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
        isActive(to)
          ? 'bg-[#8B2E3C] text-white'
          : 'text-[#222222] hover:bg-[#E9DDCF]'
      }`}
      onClick={() => setMobileMenuOpen(false)}
      data-testid={testId}
    >
      <Icon className="w-4 h-4" />
      {children}
    </Link>
  );

  return (
    <>
    <nav className="bg-white border-b-2 border-[#E9DDCF] sticky top-0 z-40" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="bg-[#8B2E3C] p-2 rounded-lg">
              <HandHeart className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl text-[#222222] hidden sm:inline">Kindness Circle</span>
          </Link>

          <div className="hidden md:flex items-center gap-2">
            <NavLink to="/" icon={Calendar} testId="nav-opportunities">Opportunities</NavLink>
            <NavLink to="/time-log" icon={Timer} testId="nav-time-log">Time Log</NavLink>
            <NavLink to="/my-signups" icon={ClipboardList} testId="nav-my-signups">My Sign-ups</NavLink>
            <NavLink to="/my-hours" icon={Clock} testId="nav-my-hours">My Hours</NavLink>
            {user?.role === 'admin' && (
              <NavLink to="/admin" icon={LayoutDashboard} testId="nav-admin">Admin</NavLink>
            )}
          </div>

          <div className="hidden md:flex items-center gap-4">
            <NotificationBell />
            <div className="text-right">
              <div className="text-sm font-medium text-[#222222]" data-testid="user-name">{user?.name}</div>
              <Link to="/profile" className="text-xs text-[#8B2E3C] hover:underline font-medium" data-testid="edit-profile-link">
                Edit Profile
              </Link>
            </div>
            <Link
              to="/profile"
              className="p-2 text-[#8B2E3C] hover:bg-[#FBF4EA] rounded-lg transition-colors"
              title="My Profile"
              data-testid="profile-button"
            >
              <User className="w-5 h-5" />
            </Link>
            <button
              onClick={handleLogout}
              className="p-2 text-[#8B2E3C] hover:bg-[#FBF4EA] rounded-lg transition-colors"
              title="Logout"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-[#222222]"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-2" data-testid="mobile-menu">
            <NavLink to="/" icon={Calendar} testId="mobile-nav-opportunities">Opportunities</NavLink>
            <NavLink to="/time-log" icon={Timer} testId="mobile-nav-time-log">Time Log</NavLink>
            <NavLink to="/my-signups" icon={ClipboardList} testId="mobile-nav-my-signups">My Sign-ups</NavLink>
            <NavLink to="/my-hours" icon={Clock} testId="mobile-nav-my-hours">My Hours</NavLink>
            {user?.role === 'admin' && (
              <NavLink to="/admin" icon={LayoutDashboard} testId="mobile-nav-admin">Admin</NavLink>
            )}
            <div className="border-t border-[#E9DDCF] pt-4 mt-4">
              <div className="px-4 mb-3">
                <div className="text-sm font-medium text-[#222222]">{user?.name}</div>
                <Link
                  to="/profile"
                  className="text-xs text-[#8B2E3C] hover:underline font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-edit-profile-link"
                >
                  Edit Profile
                </Link>
              </div>
              <Link
                to="/profile"
                className="w-full flex items-center gap-2 px-4 py-2 text-[#222222] hover:bg-[#FBF4EA] rounded-lg transition-colors font-medium"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-profile-link"
              >
                <User className="w-4 h-4" />
                My Profile
              </Link>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-4 py-2 text-[#8B2E3C] hover:bg-[#FBF4EA] rounded-lg transition-colors"
                data-testid="mobile-logout-button"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
      {/* Email verification banner temporarily disabled — verification not currently enforced. */}
    </>
  );
};

export default Navbar;